import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Popover } from "antd";
import { SmileOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import EmojiPicker from "emoji-picker-react";
import {
  createProductCategory,
  updateProductCategory,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { handleApiError } from "../../../utils/errorHandler";

const EMOJI_PICKER_WIDTH = 320;

const appendEmoji = (value, emoji) => {
  const text = String(value || "").trim();

  if (!text) return emoji;
  if (text.endsWith(emoji)) return text;

  return `${text} ${emoji}`;
};

const sanitizeArabicWithEmoji = (value = "") =>
  Array.from(value)
    .filter((char) =>
      /[\p{Script=Arabic}\p{Number}\s\p{Emoji}\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F\u200D]/u.test(
        char,
      ),
    )
    .join("");

const getArabicEmojiRules = (t) => [
  {
    required: true,
    message: t("category_name_required"),
    whitespace: true,
  },
  {
    validator: (_, value) => {
      if (!value || !String(value).trim()) return Promise.resolve();

      return sanitizeArabicWithEmoji(value) === value
        ? Promise.resolve()
        : Promise.reject(new Error(t("arabic_only_validation")));
    },
  },
];

const CategoryFormModal = ({
  open,
  mode = "add",
  initialValues = null,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setEmojiOpen(false);

    if (isEdit && initialValues) {
      form.setFieldsValue({
        id: initialValues.id,
        name: initialValues.name,
        name_ar: initialValues.name_ar,
      });
    }
  }, [open, isEdit, initialValues, form]);

  const closeModal = () => {
    form.resetFields();
    setEmojiOpen(false);
    onClose?.();
  };

  const handleEmojiSelect = ({ emoji }) => {
    if (!emoji) return;

    const { name, name_ar } = form.getFieldsValue(["name", "name_ar"]);

    form.setFieldsValue({
      name: appendEmoji(name, emoji),
      name_ar: appendEmoji(name_ar, emoji),
    });

    form.validateFields(["name", "name_ar"]).catch(() => {});
    setEmojiOpen(false);
  };

  const handleArabicChange = (e) => {
    form.setFieldsValue({
      name_ar: sanitizeArabicWithEmoji(e.target.value),
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const payload = {
        ...values,
        name: String(values.name || "").trim(),
        name_ar: String(values.name_ar || "").trim(),
      };

      const response = isEdit
        ? await updateProductCategory(payload)
        : await createProductCategory(payload);

      const responseData = response?.data || response?.response?.data;

      if (responseData?.success === true) {
        NotificationWithIcon(
          "success",
          isEdit ? t("category_updated") : t("category_added"),
          isEdit
            ? t("category_updated_successfully")
            : t("category_added_successfully"),
        );

        form.resetFields();
        setEmojiOpen(false);
        onSuccess?.(response, payload);
        onClose?.();
        return;
      }

      if (responseData?.success === false) {
        handleApiError(response, t);
      }
    } catch (err) {
      if (err?.errorFields) return;

      handleApiError(err, t);
    } finally {
      setLoading(false);
    }
  };

  const emojiPicker = (
    <div className="category-emoji-picker-wrap">
      <EmojiPicker
        width={EMOJI_PICKER_WIDTH}
        height={380}
        onEmojiClick={handleEmojiSelect}
        lazyLoadEmojis
        skinTonesDisabled
        emojiStyle="native"
        previewConfig={{ showPreview: false }}
        searchPlaceHolder={t("search")}
      />
    </div>
  );

  return (
    <>
      <style>
        {`
          .category-emoji-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 16px;
          }

          .category-emoji-header span {
            color: var(--color-text);
            font-size: 13px;
          }

          .category-emoji-picker-wrap,
          .category-emoji-picker-wrap * {
            box-sizing: border-box;
          }

          .category-emoji-picker-wrap {
            width: ${EMOJI_PICKER_WIDTH}px;
            max-width: calc(100vw - 64px);
            overflow: hidden;
          }

          .category-emoji-picker-wrap .EmojiPickerReact {
            border: 0 !important;
            box-shadow: none !important;
          }

          .category-emoji-picker-wrap .epr-body,
          .category-emoji-picker-wrap .epr-emoji-category-content {
            overflow-x: hidden !important;
          }
        `}
      </style>

      <Modal
        open={open}
        title={
          isEdit
            ? `${t("category_edit")}${
                initialValues?.name ? ` : ${initialValues.name}` : ""
              }`
            : t("category_add")
        }
        onCancel={closeModal}
        onOk={handleSubmit}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeModal} loading={loading}>
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {isEdit ? t("update") : t("category_add")}
          </Button>,
        ]}
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <div className="category-emoji-header">
            <span>{t("select_emoji")}</span>

            <Popover
              trigger="click"
              placement="bottomRight"
              open={emojiOpen}
              onOpenChange={setEmojiOpen}
              content={emojiPicker}
              overlayStyle={{ width: EMOJI_PICKER_WIDTH }}
              overlayInnerStyle={{
                padding: 0,
                overflow: "hidden",
                borderRadius: 14,
              }}
            >
              <Button icon={<SmileOutlined />} type="default">
                {t("emoji")}
              </Button>
            </Popover>
          </div>

          <Form.Item
            name="name"
            label={t("name_en")}
            rules={[
              {
                required: true,
                message: t("category_name_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input placeholder={t("category_name_example")} />
          </Form.Item>

          <Form.Item
            name="name_ar"
            label={t("name_ar")}
            rules={getArabicEmojiRules(t)}
            hasFeedback
          >
            <Input
              dir="rtl"
              placeholder={t("category_name_example")}
              onChange={handleArabicChange}
            />
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CategoryFormModal;
